import React from 'react';
import { FolderKanban, Save, Copy, CheckCircle2, Loader2 } from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import { Button } from '../../../components/ui/Button';
import { Project, Organization } from '../../../types/domain';

interface ProjectGeneralCardProps {
  currentProject: Project | null;
  currentOrg: Organization | null;
  canAdmin: boolean;
  projectName: string;
  setProjectName: (name: string) => void;
  projectDesc: string;
  setProjectDesc: (desc: string) => void;
  isSavingGeneral: boolean;
  handleSaveGeneral: (e: React.FormEvent) => void;
  copiedProjectId: boolean;
  copyProjectId: () => void;
}

export const ProjectGeneralCard: React.FC<ProjectGeneralCardProps> = ({
  currentProject,
  currentOrg,
  canAdmin,
  projectName,
  setProjectName,
  projectDesc,
  setProjectDesc,
  isSavingGeneral,
  handleSaveGeneral,
  copiedProjectId,
  copyProjectId,
}) => {
  return (
    <div className="space-y-6">
      <Card className="p-5 bg-cf-surface border-cf-border space-y-4 shadow-xs">
        <h2 className="text-sm font-semibold text-cf-textPrimary flex items-center gap-2">
          <FolderKanban size={16} className="text-cf-orange" />
          <span>Project Identity</span>
        </h2>

        <form onSubmit={handleSaveGeneral} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Project Name"
              size="xs"
              required
              disabled={!canAdmin}
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="e.g. Marketing Ingestion"
            />

            <Input
              label="Project Slug (URL identifier)"
              size="xs"
              disabled
              value={currentProject?.slug || ''}
              className="opacity-75 cursor-not-allowed"
            />
          </div>

          <Input
            label="Description (Optional)"
            size="xs"
            disabled={!canAdmin}
            value={projectDesc}
            onChange={(e) => setProjectDesc(e.target.value)}
            placeholder="Primary webhook and form ingestion datastores"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-cf-textSecondary font-medium text-xs mb-1">
                Project UUID
              </label>
              <div className="flex items-center gap-2">
                <Input
                  size="xs"
                  readOnly
                  value={currentProject?.id || ''}
                  className="w-full"
                />
                <Button
                  size="xs"
                  variant="outline"
                  onClick={copyProjectId}
                  leftIcon={copiedProjectId ? <CheckCircle2 size={13} className="text-emerald-400" /> : <Copy size={13} />}
                >
                  {copiedProjectId ? 'Copied' : 'Copy'}
                </Button>
              </div>
            </div>

            <Input
              label="Parent Organization"
              size="xs"
              disabled
              value={currentOrg?.name || 'Ericius Org'}
              className="opacity-75 cursor-not-allowed"
            />
          </div>

          {canAdmin && (
            <div className="flex justify-end pt-2">
              <Button
                type="submit"
                variant="primary"
                size="xs"
                disabled={isSavingGeneral || !projectName.trim()}
                leftIcon={isSavingGeneral ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              >
                {isSavingGeneral ? 'Saving...' : 'Save Project Details'}
              </Button>
            </div>
          )}
        </form>
      </Card>
    </div>
  );
};
